/**
 * People and what to show — `docs/activity.md` §10.2 (Board 13 C), and the
 * iPad's 420 popover of the same thing with **To book** under it (§10.1).
 *
 * *Saved* as chips; *People* as 44 px rows — a 22 px box in the person's
 * colour, their face, the name and *<role> · <days>* — you first, the
 * largest team open and every other one a row of its own that opens it,
 * a branch's **Unassigned**, and a lock on anybody this person cannot see;
 * *Show* as the four switches; then **Save as a group** · **Show <n>**.
 * The people wait for **Show**; the switches apply at once, like the side
 * panel's.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { toneOf, type ActivityPerson, type ShowState } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { activityRequest, peopleQuery } from './api.js'
import { useActivity } from './activity-context.js'
import { Face, UnassignedFace } from './faces.js'
import { byDepartment } from './departments.js'
import { Glyph } from './glyphs.js'
import type { ToBookCard } from './calendar/side-panel.js'

/* What each layer looks like, Board 5 D — said where there is room for it (the iPad's 420 popover). */
const SHOW_ROWS: { key: keyof ShowState; label: string; looks: string }[] = [
  { key: 'visits', label: 'Site visits & meetings', looks: 'green · blue edge' },
  { key: 'tasks', label: 'Tasks with a time', looks: 'amber chip' },
  { key: 'blocks', label: 'Time blocks', looks: 'hatched' },
  { key: 'drives', label: 'Drive times', looks: 'in the time row' },
]

function Head({ children }: { children: ReactNode }) {
  return <div className="mb-0.5 mt-2.5 text-[11px] font-semibold uppercase tracking-[.06em] text-text-faint">{children}</div>
}

/** The 22 px box in the person's colour (Board 13 C). */
function Box({ colour, on, muted = false }: { colour: string | null; on: boolean; muted?: boolean }) {
  const edge = muted ? 'rgb(var(--color-text-muted))' : toneOf(colour).edge
  return (
    <span aria-hidden className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[6px] text-white"
      style={{ border: `1.8px solid ${edge}`, background: on ? edge : 'rgb(var(--color-surface))' }}>
      {on && <Glyph name="check" size={14} stroke={3} />}
    </span>
  )
}

/** A 40 × 24 switch (Board 13 C). */
function Switch({ on }: { on: boolean }) {
  return (
    <span aria-hidden className={cn('inline-flex h-6 w-10 shrink-0 items-center rounded-full p-0.5',
      on ? 'justify-end bg-accent' : 'justify-start bg-line-strong')}>
      <i className="block h-5 w-5 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,.25)]" />
    </span>
  )
}

export interface PeopleDraft {
  picked: Set<string>
  branches: Set<string>
}

/** The body: the people, the switches and (on an iPad) To book. The footer is the frame's. */
export function PeopleSheetBody({
  draft, setDraft, onGroup, showToBook = false,
}: {
  draft: PeopleDraft
  setDraft: (next: PeopleDraft) => void
  /** A saved group, or *Just me*: applies at once (§6.6). */
  onGroup: (group: { id: string | null; memberIds: string[] }) => void
  showToBook?: boolean
}) {
  const { directory, me, state, change, people } = useActivity()
  const [q, setQ] = useState('')
  const [toBook, setToBook] = useState<ToBookCard[]>([])

  useEffect(() => {
    if (!showToBook || people.length === 0) return undefined
    let live = true
    activityRequest<{ items: ToBookCard[] }>(`/activity/to-book?people=${peopleQuery(people)}`)
      .then((answer) => { if (live) setToBook(answer.items) })
      .catch(() => { if (live) setToBook([]) })
    return () => { live = false }
  }, [showToBook, people])

  /*
   * Everyone, by department, every department open with its name over it
   * (Aaron, Oct 23 — Board 13 C folded every team but the first into a row),
   * and a name or a department searches it. `byDepartment` is the quick
   * list's own grouping, so a phone and a desktop list the same people the
   * same way.
   */
  const query = q.trim().toLowerCase()
  const sections = useMemo(() => byDepartment((directory?.people ?? []).filter((person) => {
    if (person.id === me.user.id) return false
    const department = [person.role, person.place].filter(Boolean).join(' · ')
    return !query || person.name.toLowerCase().includes(query) || department.toLowerCase().includes(query)
  }), people), [directory, me.user.id, people, query])

  if (!directory) return <div className="py-3 text-[13px] text-text-muted">Loading…</div>
  const you = directory.people.find((p) => p.id === me.user.id)

  const toggle = (id: string) => {
    const picked = new Set(draft.picked)
    if (picked.has(id)) picked.delete(id)
    else picked.add(id)
    setDraft({ ...draft, picked })
  }
  const toggleBranch = (id: string) => {
    const branches = new Set(draft.branches)
    if (branches.has(id)) branches.delete(id)
    else branches.add(id)
    setDraft({ ...draft, branches })
  }

  const personRow = (person: ActivityPerson, sub: string) => (
    <button key={person.id} type="button" role="checkbox" aria-checked={draft.picked.has(person.id)}
      onClick={() => toggle(person.id)}
      className="flex h-11 w-full items-center gap-3 border-b border-line-faint text-left">
      <Box colour={person.colour} on={draft.picked.has(person.id)} />
      <Face person={person} size={30} self={person.id === me.user.id} />
      <span className="flex min-w-0 flex-col">
        <b className="truncate text-[14.5px] font-semibold">{person.name}{person.id === me.user.id ? ' (you)' : ''}</b>
        <span className="truncate text-[12px] text-text-muted">{sub}</span>
      </span>
    </button>
  )

  return (
    <div className="flex flex-col">
      <Head>Saved</Head>
      <div className="my-1 flex flex-wrap gap-1.5">
        {directory.groups.map((group) => (
          <button key={group.id} type="button" onClick={() => onGroup({ id: group.id, memberIds: group.memberIds })}
            className={cn('inline-flex h-9 items-center rounded-full border px-3 text-[13px] font-semibold',
              state.people.groupId === group.id ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface')}>
            {group.name}
          </button>
        ))}
        <button type="button" onClick={() => onGroup({ id: null, memberIds: [me.user.id] })}
          className="inline-flex h-9 items-center rounded-full border border-line bg-surface px-3 text-[13px] font-semibold">
          Just me
        </button>
      </div>

      <Head>People</Head>
      <label className="my-1.5 flex h-10 items-center gap-2 rounded-[10px] border border-line px-3 text-text-faint
        focus-within:shadow-[0_0_0_2px_rgb(var(--color-accent-ring))]">
        <Glyph name="search" size={16} />
        <input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Find a person"
          aria-label="Find a person"
          className="min-w-0 flex-1 bg-transparent text-[14.5px] text-text outline-none placeholder:text-text-faint focus-visible:shadow-none" />
      </label>
      {you && (!query || you.name.toLowerCase().includes(query)) && personRow(you, you.role ?? '')}
      {sections.map(([key, list]) => (
        <div key={key} data-department={key}>
          <div className="flex items-center gap-2 pb-0.5 pt-2.5 text-[12.5px] text-text-muted">
            <span className="inline-flex w-[22px] justify-center text-text-faint"><Glyph name="people" size={15} /></span>
            <b className="truncate font-semibold text-text">{key}</b>
            <span>{list.length} {list.length === 1 ? 'person' : 'people'}</span>
          </div>
          {list.map((person) => personRow(person, [person.role, person.workDays].filter(Boolean).join(' · ')))}
        </div>
      ))}
      {directory.unassigned.map((unit) => (
        <button key={unit.orgUnitId} type="button" role="checkbox" aria-checked={draft.branches.has(unit.orgUnitId)}
          onClick={() => toggleBranch(unit.orgUnitId)}
          className="flex h-11 w-full items-center gap-3 border-b border-line-faint text-left">
          <Box colour={null} on={draft.branches.has(unit.orgUnitId)} muted />
          <UnassignedFace size={30} />
          <span className="flex min-w-0 flex-col">
            <b className="truncate text-[14.5px] font-semibold">Unassigned · {unit.name}</b>
            <span className="truncate text-[12px] text-text-muted">texts, emails and tasks nobody owns</span>
          </span>
        </button>
      ))}
      {directory.locked.map((person) => (
        <div key={person.id} className="flex h-11 items-center gap-3 border-b border-line-faint text-text-faint">
          <span className="inline-flex w-[22px] justify-center"><Glyph name="lock" size={15} /></span>
          <Face person={{ ...person, colour: null }} size={30} />
          <span className="flex min-w-0 flex-col">
            <b className="truncate text-[14.5px] font-semibold">{person.name}</b>
            <span className="truncate text-[12px]">ask an admin to see this calendar</span>
          </span>
        </div>
      ))}

      <Head>Show</Head>
      {SHOW_ROWS.map((row) => (
        <button key={row.key} type="button" role="switch" aria-checked={state.calendar.show[row.key]}
          onClick={() => change({ calendar: { show: { [row.key]: !state.calendar.show[row.key] } } })}
          className="flex h-[38px] w-full items-center gap-2.5 border-b border-line-faint text-left text-[14.5px]">
          <span className="flex-1 text-text">{row.label}</span>
          {showToBook && <span className="text-[11.5px] text-text-faint">{row.looks}</span>}
          <Switch on={state.calendar.show[row.key]} />
        </button>
      ))}

      {showToBook && toBook.length > 0 && (
        <>
          <Head>To book · {toBook.length}</Head>
          <div className="mt-1 flex flex-col gap-2">
            {toBook.map((card) => (
              <div key={card.appointmentId ?? card.jobId}
                className="rounded-[10px] border border-dashed border-line-strong bg-surface px-2.5 py-[9px]">
                <b className="block text-[12.5px]">{card.title}</b>
                <span className="text-[11px] text-text-muted">
                  {card.propertyName}{card.city ? ' · ' : ''}
                  {card.city && <span className="whitespace-nowrap">{card.city}{card.state ? `, ${card.state}` : ''}</span>}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
